import { useMutation } from '@apollo/client'
import { Formik, FormikHelpers } from 'formik'
import { isArray, isEmpty } from 'lodash'
import { FormattedMessage, useIntl, defineMessages } from 'react-intl'
import { ReactElement, useRef } from 'react'
import styled from 'styled-components'

import { Button } from 'components/v2/Button'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { Input } from 'components/Input'
import { Row } from 'components/v2/Row'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { SelectRoutingProjectsTags } from 'features/RoutingProjects/components/SelectRoutingProjectsTags'
import { IRoutingProjectModel } from 'features/RoutingProjects/types'
import { CREATE_ROUTING_PROJECT, CHANGE_ROUTING_PROJECT } from 'features/RoutingProjects/mutations'
import { ROUTING_PROJECT_FRAGMENT, ROUTING_PROJECT_FRAGMENT_NAME } from 'features/RoutingProjects/queries'
import { ICreateRoutingProjectForm } from 'features/RoutingProjects/types'
import { getUnprocessableErrors } from 'utils/error'
import { schema } from './schema'

const ModalContent = styled(Modal.Content)`
  padding-bottom: 30px;
`

const messages = defineMessages({
  namePlaceholder: {
    id: 'routingProjects.create.name.placeholder',
    defaultMessage: 'Enter a name'
  },
  tagsPlaceholder: {
    id: 'routingProjects.create.tags.placeholder',
    defaultMessage: 'Add tags'
  }
})

interface ICreateRoutingProjectModalProps {
  closeModal: () => void
  routingProject?: IRoutingProjectModel
}

export const CreateRoutingProjectModal = ({
  closeModal,
  routingProject
}: ICreateRoutingProjectModalProps): ReactElement | null => {
  const intl = useIntl()
  const newProjectRef = useRef<ICreateRoutingProjectForm | null>(null)

  const [createRoutingProject, { error: createRoutingProjectError }] = useMutation(CREATE_ROUTING_PROJECT, {
    update(cache, { data }) {
      cache.modify({
        fields: {
          routingProjects(existingRoutingProjects = { items: [], total: 0 }) {
            const newRoutingProjectRef = cache.writeFragment({
              data: {
                __typename: 'RoutingProject',
                id: data?.createRoutingProject.id || '',
                name: newProjectRef.current?.name || '',
                tags: newProjectRef.current?.tags || [],
                plans: [],
                datasets: []
              },
              fragment: ROUTING_PROJECT_FRAGMENT,
              fragmentName: ROUTING_PROJECT_FRAGMENT_NAME
            })
            return {
              items: [newRoutingProjectRef, ...existingRoutingProjects.items],
              total: existingRoutingProjects.total + 1
            }
          }
        }
      })
    }
  })
  const [editRoutingProject, { error: editRoutingProjectError }] = useMutation(CHANGE_ROUTING_PROJECT, {
    update(cache) {
      cache.modify({
        id: cache.identify({
          __typename: 'RoutingProject',
          id: routingProject?.id
        }),
        fields: {
          name() {
            return newProjectRef.current?.name
          },
          tags() {
            return newProjectRef.current?.tags
          }
        }
      })
    }
  })

  const handleSubmit = async (
    values: ICreateRoutingProjectForm,
    { setSubmitting }: FormikHelpers<ICreateRoutingProjectForm>
  ): Promise<void> => {
    try {
      newProjectRef.current = values
      if (routingProject?.id) {
        await editRoutingProject({
          variables: {
            input: {
              id: routingProject.id,
              name: values.name,
              tags: values.tags
            }
          }
        })
      } else {
        await createRoutingProject({
          variables: {
            input: {
              name: values.name,
              tags: values.tags
            }
          }
        })
      }
      closeModal()
    } catch (ex) {
      // Do nothing
    }
    setSubmitting(false)
  }

  const initialValues: ICreateRoutingProjectForm = {
    name: routingProject?.name || '',
    tags: routingProject?.tags || []
  }

  return (
    <Modal.Container>
      <Modal.Header smallMarginBottom>
        <Heading.H5>
          <FormattedMessage id="routingProjects.create.title" defaultMessage="Create Project" />
        </Heading.H5>
      </Modal.Header>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema(intl)}>
        {(props): ReactElement => {
          const { handleChange, setFieldValue, handleBlur, touched, errors, values, isSubmitting } = props

          const handleTagsChange = (tags: string[]): void => {
            setFieldValue('tags', tags)
          }

          return (
            <form onSubmit={props.handleSubmit}>
              <ModalContent>
                <Row>
                  <Column>
                    <Input
                      id="name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched.name}
                      errors={errors.name}
                      label={<FormattedMessage id="routingProjects.create.name.label" defaultMessage="Name" />}
                      placeholder={intl.formatMessage(messages.namePlaceholder)}
                    />
                  </Column>
                </Row>
                <Row>
                  <Column>
                    <SelectRoutingProjectsTags
                      id="tags"
                      values={values.tags}
                      onChange={handleTagsChange}
                      onBlur={handleBlur}
                      label={<FormattedMessage id="routingProjects.create.tags.label" defaultMessage="Tags" />}
                      placeholder={intl.formatMessage(messages.tagsPlaceholder)}
                    />
                    {touched.tags && isArray(errors.tags) && !isEmpty(errors.tags) && (
                      <>
                        {errors.tags.map((error: string, index: number) => (
                          <ErrorMessage key={index}>{error}</ErrorMessage>
                        ))}
                      </>
                    )}
                  </Column>
                </Row>
              </ModalContent>
              <Modal.Footer>
                {editRoutingProjectError && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(editRoutingProjectError)}>
                      <FormattedMessage
                        id="routingProjects.edit.error"
                        defaultMessage="An error occurred while editing the project"
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                {createRoutingProjectError && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(createRoutingProjectError)}>
                      <FormattedMessage
                        id="routingProjects.create.error"
                        defaultMessage="An error occurred while creating the project"
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button type="button" kind="cancel" onClick={(): void => closeModal()}>
                    <FormattedMessage id="routingProjects.create.cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button kind="primary" loading={isSubmitting}>
                    <FormattedMessage id="routingProjects.create.submit" defaultMessage="Save" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
